// 必要なライブラリやコンポーネントをインポート
import React, { useEffect, useState } from "react";
import { Grid, Box, Modal, Link, Button, useMediaQuery, Stack } from '@mui/material';
import { styled, useTheme, alpha, makeStyles } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase'; // 入力フィールド
import Drawer from '@mui/material/Drawer'; // スライド式のサイドバー
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline'; // ベースとなるスタイル
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu'; // メニューアイコン
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search'; // 検索アイコン
import UserChat from "./UserChat"; // チャット表示コンポーネント
import { useNavigate } from "react-router-dom"; // 画面遷移用
import { useRecoilState } from "recoil"; // グローバル状態管理
import userState from "../../recoil/atoms/userState"; // ユーザー状態を管理するRecoilのアトム
import logoutIcon from "../../statics/img/logout.png"; // ログアウトアイコン

/** 検索バー style設定 */
const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius, // 角を丸める
  backgroundColor: alpha(theme.palette.common.black, 0.15), // 背景色を設定
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.25), // ホバー時の背景色
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

/** 検索バーアイコン style設定 */
const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2), // アイコンの余白
  height: '100%',
  position: 'absolute', // 絶対配置
  pointerEvents: 'none', // ユーザーの操作を無効化
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

/** 検索バー入力欄 style設定 */
const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`, // アイコン分の余白を設定
    transition: theme.transitions.create('width'), // 幅変更のアニメーション
    width: 900, // デフォルト幅
    [theme.breakpoints.down('lg')]: { width: 600 },
    [theme.breakpoints.down('md')]: { width: 300 },
    [theme.breakpoints.down('sm')]: { width: 100 },
  },
}));

// サイドメニューの幅
const drawerWidth = 240;

// AppBarのスタイル設定
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: drawerWidth,
  }),
}));

/** Drawerのヘッダーのスタイル設定 */
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar, // ツールバーのスタイルを適用
  justifyContent: 'flex-start',
}));

/** 検索テンプレートのプロパティインターフェース */
export interface SearchTemplateProps {
  children: React.ReactNode; // 子コンテンツ
  title: string; // 画面タイトル
  isEntry?: boolean; // エントリーモードか
  isMargin?: boolean; // マージンを有効にするか
}

/** Main部分のスタイル設定 */
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(0), // パディング
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  }),
}));

/** 
 * UserFrameコンポーネント
 * - 共通フレームとして、ヘッダー・メインコンテンツ・フッターを含む。
 * - Drawerによるサイドメニューの表示や、チャット機能、ログアウトなどの操作を提供。
 */
const UserFrame: React.FC<SearchTemplateProps> = ({
  children,
  title,
  isEntry,
  isMargin,
}) => {
  // 現在のURLを取得
  const currentURL = window.location.href;

  // ページ遷移時のイベントリスナークリア
  function cancelBeforeUnload(event: BeforeUnloadEvent) {}
  useEffect(() => {
    if (!currentURL.includes('/problems')) {
      window.onbeforeunload = cancelBeforeUnload;
    }
  }, []);

  const theme = useTheme(); // テーマの取得
  const navigate = useNavigate(); // 画面遷移用
  const [, setUserData] = useRecoilState(userState); // グローバルユーザー状態管理

  // Drawerの開閉状態を管理
  const [open, setOpen] = useState(false);
  const handleDrawerOpen = () => setOpen(true);
  const handleDrawerClose = () => setOpen(false);

  // チャットモーダルの開閉状態を管理
  const [chatOpen, setChatOpen] = useState(false);
  const handleOpen = () => setChatOpen(true);
  const handleClose = () => setChatOpen(false);

  // モーダルのスタイル
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  // 画面サイズに応じたレスポンシブデザイン対応
  const matches: boolean = useMediaQuery("(min-width:640px)");

  // ヘッダーのタイトル表示
  const HomeLink = () => (
    <Typography
      variant={matches ? 'h6' : 'subtitle1'}
      noWrap
      sx={{
        flexGrow: 1,
        width: 200,
        minWidth: matches ? 140 : 100,
        '&:hover': { opacity: [0.7, 0.7, 0.7] },
      }}
      component="div"
      onClick={() => navigate(!isEntry ? "/home" : "/entry/home")}
    >
      ZG-Education
    </Typography>
  );

  // 検索バーまたはタイトル表示
  const HeadTitle = () => (
    <Grid container direction="column" alignItems="center">
      {title === '' ? (
        <Search>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder={matches ? "資料を検索　例：Java、AWS等" : "資料を検索"}
            inputProps={{ 'aria-label': 'search' }}
          />
        </Search>
      ) : (
        <Typography variant={matches ? 'h4' : 'h5'} sx={{ width: '100%' }}>
          {matches ? title : title!.length > 13 ? title!.slice(0, 13) + '...' : title}
        </Typography>
      )}
    </Grid>
  );

  return (
    <Box sx={{ display: 'flex', maxWidth: '100%' }}>
      {/* ヘッダー部分 */}
      <CssBaseline />
      <AppBar position="fixed" open={open} sx={{ backgroundColor: 'white', color: 'black' }}>
        <Box sx={{ backgroundColor: 'darkgrey', color: '#eeeeee', height: 28, paddingTop: 0.5, paddingLeft: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            ZealGroup Educational Materials Website
          </Typography>
        </Box>
        <Toolbar>
              <HomeLink />
              <HeadTitle />
          <div style={{ flexGrow: 1 }}></div>
          {!isEntry ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerOpen}
              sx={{ ...(open && { display: 'none' }) }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box>
              <IconButton
                onClick={() => {
                  if (window.confirm("ログアウトしますか？")) {
                    sessionStorage.clear();
                    setUserData({
                      email: '',
                      userId: 0,
                      userName: '',
                      userType: 0,
                      token: '',
                      companyName: '',
                    });
                    navigate("/");
                  }
                }}
                sx={{ '&:hover': { opacity: [0.7, 0.7, 0.7] } }}
              >
                <img src={logoutIcon} height={28} width={28} alt="" />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* メインコンテンツ部分 */}
      <Main open={open}>
        <DrawerHeader />
        <Box sx={{ height: 28, width: '100%' }} />
        {children}
        <Box sx={{ height: '3rem', width: '100%' }} />

        {/* フッター部分 */}
        <AppBar position="fixed" sx={{ top: 'auto', bottom: 0, backgroundColor: '#000000' }}>
          <Box
            sx={{
              bgcolor: 'black',
              color: 'white',
              height: 64,
              bottom: 0,
              position: "absolute",
              width: '100%',
            }}
          >
            <Grid container direction="column" alignItems="center">
              <Grid item xs={12}>
                {isEntry || true ? (
                  <Box height={'1rem'} />
                ) : (
                  <Box margin={2}>
                    <Box>
                      <Link href="/contact" underline="hover" color="inherit">
                        お問い合わせ
                      </Link>
                    </Box>
                    <Box>
                      <Link href="/sitemap" underline="hover" color="inherit">
                        サイトマップ
                      </Link>
                    </Box>
                    <Box>
                      <Link href="/terms" underline="hover" color="inherit">
                        利用規約
                      </Link>
                    </Box>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ fontSize: 10 }} height={'1.5rem'}>
                  Copyright © ZEAL HOLDINGS All Rights Reserved.
                </Box>
                <Stack
                    direction={"row"}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                    fontSize={11}
                >
                    <Link 
                        href="https://www.cross-zeal.com/company/growzealcompanyoutline" 
                        underline="none" 
                        target='_blank'
                        color={"white"}
                    >
                        運営会社
                    </Link>
                    <Link 
                        href="/term-of-service" 
                        underline="none" 
                        target='_blank'
                        color={"white"}
                    >
                        利用規約
                    </Link>
                    <Link 
                        href="/privacy-policy" 
                        underline="none" 
                        target='_blank'
                        color={"white"}
                    >
                        プライバシーポリシー
                    </Link>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </AppBar>
      </Main>

      {/* サイドメニューDrawer */}
      <Drawer
        sx={{
          width: open ? drawerWidth : 2,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: drawerWidth },
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          <ListItem key="Myアカウント" disablePadding>
            <ListItemButton href="/myAccount">
              <ListItemText primary="Myアカウント" />
            </ListItemButton>
          </ListItem>
        </List>
        <List>
          {['インフラ', 'システム'].map((text, index) => (
            <ListItem key={text} disablePadding>
              <ListItemButton href="/collection">
                <ListItemText primary={text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {/*<List>
          <ListItem key="教育担当者へチャット" disablePadding>
            <ListItemButton onClick={handleOpen}>
              <ListItemText primary="教育担当者へチャット" />
            </ListItemButton>
          </ListItem>
        </List>*/}
        <Divider />
        <List>
          <ListItem key="ログアウト" disablePadding>
            <ListItemButton
              href="/login"
              onClick={() => {
                sessionStorage.clear();
                setUserData({
                  email: '',
                  userId: 0,
                  userName: '',
                  userType: 0,
                  token: '',
                  companyName: '',
                });
              }}
            >
              <ListItemText primary="ログアウト" />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>

      {/* チャットモーダル */}
      <Modal
        open={chatOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <UserChat height={Math.floor(document.documentElement.clientHeight * 0.7)} />
        </Box>
      </Modal>
    </Box>
  );
};

// UserFrameコンポーネントをデフォルトエクスポート
export default UserFrame;
